import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import React from 'react';
import { AutomationRulesDropdown } from '../components/automation-rules-dropdown.component';
import {
    AutomationRulesRadioButtonsConditionComponent,
    ButtonValues,
} from '../components/automation-rules-radio-buttons.component';
import { FieldConditional } from '../components/field-conditional';
import {
    AutomationRulesDataKeys,
    RuleConditionSourceFieldValues,
    TriggerConditions,
    TriggerExternalConditions,
} from '../constants/backend-enums';
import { TRANSL_STRG_AUT_RULES } from '../constants/common';
import { OnChangeStateTypeValues } from '../constants/frontend-enums';
import { FieldsContainer } from '../styled/automation-rules.style';
import { ExternalOptions, OnChangeAutomationRuleStateData } from '../types/automation-rules.type';

import { useTranslation } from 'core/i18n/i18n';
import { validate as uuidValidate } from 'uuid';

const TRIGGER_CONDITIONS_KEYS = Object.keys(TriggerConditions);
const TRIGGER_CONDITIONS_VALUES = Object.values(TriggerConditions);

const TRIGGER_TYPES = TRIGGER_CONDITIONS_KEYS.map((el, index) => {
    const value = TRIGGER_CONDITIONS_VALUES[index];
    return { id: value, value, label: TRANSL_STRG_AUT_RULES + el };
});

const SOURCE_KEYS = Object.keys(RuleConditionSourceFieldValues);
const SOURCE_VALUES = Object.values(RuleConditionSourceFieldValues);

const SOURCE_TYPES = SOURCE_KEYS.map((el, index) => {
    const value = SOURCE_VALUES[index];
    return { id: value, value, label: TRANSL_STRG_AUT_RULES + el };
});

interface FieldsData {
    field: string;
    identifier: string;
    value: string;
    type: string;
}

interface ConditionalTriggerContainerProps {
    id?: number;
    fieldsData?: FieldsData;
    externalOptions?: ExternalOptions[];
    onChange: (data: OnChangeAutomationRuleStateData) => void;
}

export const ConditionalTriggerContainer = (props: ConditionalTriggerContainerProps) => {
    const { id, fieldsData, externalOptions = [], onChange } = props;

    const { t } = useTranslation();

    const { type, field, value: fieldValue, identifier } = fieldsData || {};

    const [selectedCategory, setSelectedCategory] = React.useState<string>(field ?? '');

    const secondaryFixedData: Record<string, { props: Record<string, unknown> }> = {
        [TriggerConditions.source]: {
            props: {
                options: SOURCE_TYPES,
                placeholder: t(`${TRANSL_STRG_AUT_RULES}choose_value`),
                fieldCondition: 'dropdown',
                type: OnChangeStateTypeValues.keep_value,
            },
        },
        [TriggerExternalConditions.custom_field]: {
            props: {
                placeholder: t(`${TRANSL_STRG_AUT_RULES}enter_text`),
                fieldCondition: 'input',
                type: OnChangeStateTypeValues.keep_value,
            },
        },
    };

    const onChangeHandler = (data: OnChangeAutomationRuleStateData) => {
        const { value } = data;
        data.index = id;

        onChange(data);

        if (uuidValidate(value)) {
            setSelectedCategory(TriggerExternalConditions.custom_field);
            return;
        }
        if (TRIGGER_CONDITIONS_VALUES.includes(value as TriggerConditions)) {
            setSelectedCategory(value as TriggerConditions);
        }
    };

    const getSecondaryData = () => {
        const data = secondaryFixedData?.[selectedCategory]?.props ?? {};
        if (selectedCategory === TriggerExternalConditions.custom_field) {
            return {
                ...data,
                defaultValue: fieldValue ?? '',
            };
        }
        return data;
    };

    const getTriggerOptions = () => [...TRIGGER_TYPES, ...externalOptions];

    const getDefaultSelectedOption = () => {
        if (field === TriggerExternalConditions.custom_field) {
            return externalOptions?.find((el) => el.id === identifier)?.id;
        }
        return field;
    };

    const secondaryData = getSecondaryData();
    const triggerOptions = getTriggerOptions();
    const defaultSelectedOption = getDefaultSelectedOption();
    return (
        <>
            <PocTypography variant="subtitle1">{t(`${TRANSL_STRG_AUT_RULES}operator_field`)}</PocTypography>
            <FieldsContainer>
                <AutomationRulesDropdown
                    name={AutomationRulesDataKeys.field}
                    options={triggerOptions}
                    placeholder={t(`${TRANSL_STRG_AUT_RULES}choose_field`)}
                    type={OnChangeStateTypeValues.keep_value}
                    onChange={onChangeHandler}
                    defaultSelectedOption={defaultSelectedOption}
                />
            </FieldsContainer>
            {selectedCategory && (
                <>
                    <AutomationRulesRadioButtonsConditionComponent
                        name={AutomationRulesDataKeys.type}
                        defaultValue={
                            Object.values(ButtonValues).includes(type as ButtonValues)
                                ? (type as ButtonValues)
                                : undefined
                        }
                        onChange={onChangeHandler}
                        type={OnChangeStateTypeValues.keep_value}
                    />
                    <FieldConditional
                        name={AutomationRulesDataKeys.value}
                        defaultSelectedOption={fieldValue}
                        onChange={onChangeHandler}
                        {...secondaryData}
                    />
                </>
            )}
        </>
    );
};
